import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CheckCheck, Loader2, Inbox } from "lucide-react";
import toast from "react-hot-toast";
import { NotificationService } from "@/services/notificationService";
import { SuperAdminNotificationService } from "@/services/superAdminNotificationService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import useLiveResource from "@/hooks/useLiveResource";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { TooltipProvider } from "@/components/ui/Tooltip";
import NotificationItem from "./NotificationItem";
import { notificationLink } from "@/utils/notificationLink";

// One page of the inbox.
const PER_PAGE = 20;

/* ───────────────── Main Component ───────────────── */

export const NotificationBell = () => {
  const isSuperAdmin = useSelector((state) => !!state.auth?.user?.isSuperAdmin);
  const service = isSuperAdmin ? SuperAdminNotificationService : NotificationService;
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Which page of the inbox is loaded, and whether there is more behind it.
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState(null);

  const openRef = useRef(open);
  // A dead component sets no state: any fetch can land after the header
  // unmounts on sign-out or a route change.
  const mountedRef = useRef(true);
  // The newest list request wins. Opening the panel, a WebSocket event and
  // "Load more" can all be in flight together, and an older reply must not
  // overwrite a newer one.
  const listRequestRef = useRef(0);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ───────── Fetch unread count ───────── */

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await service.fetUnreadCount();

      if (res.success && mountedRef.current) {
        setUnreadCount(res?.data?.unread_count ?? 0);
      }
    } catch (e) {
      // Silent — this is a background reconcile; the panel's own fetch
      // surfaces real errors.
      console.error("Failed to fetch unread count:", e);
    }
  }, [service]);

  /* ───────── Fetch notifications ───────── */

  // `pageToLoad` 1 replaces the list; a later page is appended.
  const loadPage = useCallback(
    async (pageToLoad) => {
      const requestId = listRequestRef.current + 1;
      listRequestRef.current = requestId;

      const isFirstPage = pageToLoad === 1;

      if (isFirstPage) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      try {
        const res = await service.fetNotificationList({
          page: pageToLoad,
          per_page: PER_PAGE,
        });

        // A newer request went out while this one was in flight, or we are gone.
        if (!mountedRef.current || listRequestRef.current !== requestId) return;

        if (res.success) {
          const rows = res?.data?.notifications ?? [];
          setNotifications((prev) => (isFirstPage ? rows : [...prev, ...rows]));
          setUnreadCount(res?.data?.unread_count ?? 0);
          setTotalPages(res?.data?.pagination?.total_pages ?? 1);
          setPage(pageToLoad);
        } else if (isFirstPage) {
          // A 200 that is not a success is still a failure — showing the empty
          // state here would tell the user "you're all caught up" about a list
          // the server never sent.
          setError(res?.message || "We couldn't reach your notifications.");
        } else {
          toast.error("Couldn't load older notifications. Please try again.");
        }
      } catch (e) {
        if (!mountedRef.current || listRequestRef.current !== requestId) return;

        // The server's own sentence when it sent one. `e.message` is axios'
        // "Request failed with status code 500", which tells the user nothing.
        // Coerced to a string: `apiErrorMessageFormat` falls back to the raw
        // response body, and an object rendered into JSX throws.
        const message = apiErrorMessageFormat(e, "We couldn't reach your notifications.");
        const text = typeof message === "string" ? message : "We couldn't reach your notifications.";

        if (isFirstPage) setError(text);
        else toast.error(text);
      } finally {
        if (mountedRef.current && listRequestRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [service],
  );

  const fetchNotifications = useCallback(() => loadPage(1), [loadPage]);

  /* ───────── Unread count: on mount, then reconciled ───────── */

  // The WebSocket is an accelerator, not the source of truth. A missed event
  // used to leave the badge wrong for the rest of the session, because nothing
  // ever asked again — so ask on mount, and ask the moment the tab comes back
  // to the front. The interval poll is gone: a reconnect and a detected
  // sequence gap both refetch through `useLiveResource` below, which is what
  // the poll was covering for. This one stays because a tab that slept through
  // the close never saw either.
  useEffect(() => {
    fetchUnreadCount();

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      fetchUnreadCount();
      if (openRef.current) fetchNotifications();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchUnreadCount, fetchNotifications]);

  /* ───────── Fetch when opened ───────── */

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  /* ───────── WebSocket refresh ───────── */

  // Both a created and a read event refetch. A read is not "set the badge to
  // zero": another tab, or another super admin's own bell, may have read one
  // of several, and the envelope carries no count to forward. A SuperAdmin
  // session and a tenant session subscribe to their own resource — the two
  // inboxes never notify each other.
  useLiveResource({
    keys: isSuperAdmin ? "superadmin_notification" : "notification",
    onChange: () => {
      fetchUnreadCount();
      if (openRef.current) fetchNotifications();
    },
  });

  /* ───────── Click a row: go where it points, and mark it read ─────────
     A click used to mark the row read and nothing else, so the panel stayed
     open and the reader kept their place in the list. That left every
     notification a dead end: "Candidate Undo Request" named a candidate and
     offered no way to reach them. A click now opens what the row is about,
     closing the panel on the way, and marks it read when it was not. The one
     place that maps a row to a route is `notificationLink`. */

  const handleMarkRead = async (id) => {
    // Optimistic for the row, then the server for the count.
    //
    // The count used to be arithmetic on a local guess, and the guess was
    // wrong whenever the server disagreed: clicking a row another tab had
    // already read dropped the badge for a row that was already counted (the
    // server answers 200 for an already-read row, by design). The row grays
    // immediately because that is this reader's own action; the number comes
    // from the one place that knows it.
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const res = await service.notificationMarkRead([id]);

      if (!res.success) throw new Error(res?.message || "Failed to mark as read");

      fetchUnreadCount();
    } catch (e) {
      toast.error("Couldn't mark notification as read. Please try again.");
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: false } : n)));
      setUnreadCount((prev) => prev + 1);
    }
  };

  const handleOpen = (notification) => {
    if (!notification.is_read) handleMarkRead(notification.id);

    const destination = notificationLink(notification);
    if (destination) {
      setOpen(false);
      navigate(destination);
    }
  };

  /* ───────── Mark all read ───────── */

  const handleMarkAllRead = async () => {
    setMarkingAll(true);

    try {
      const res = await service.notificationMarkAllRead();

      if (res.success) {
        setNotifications((prev) => prev.map((notification) => ({ ...notification, is_read: true })));
        // Not `setUnreadCount(0)`: a notification landing between the server's
        // UPDATE and this line is unread on the server and would be grayed out
        // here with the badge reading zero.
        fetchUnreadCount();
        if (openRef.current) fetchNotifications();
      }
    } catch (e) {
      toast.error("Couldn't mark all notifications as read. Please try again.");
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="Notifications"
          className="relative h-10 w-10 rounded-full border-text-primary/40 bg-glass hover:text-text-primary text-text-primary hover:bg-background-tertiary aria-expanded:border-tone-blue/40 aria-expanded:bg-tone-blue-soft aria-expanded:text-tone-blue"
        >
          <Bell size={17} strokeWidth={1.8} />

          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-2 border-background-primary bg-tone-blue px-1 text-[9px] font-semibold text-text-invert">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={10}
        className="flex w-[380px] max-w-[calc(100vw-24px)] max-h-[min(560px,calc(100vh-96px))] flex-col gap-0 overflow-hidden rounded-2xl border border-border-primary bg-background-secondary p-0 text-text-primary shadow-2xl"
      >
        {/* ───────── Header ───────── */}

        <div className="flex flex-shrink-0 items-center bg-background-primary justify-between gap-3 border-b border-border-primary px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>

            {unreadCount > 0 && <span className="rounded-full bg-tone-blue-soft px-1.5 py-0.5 text-[10px] font-semibold text-tone-blue">{unreadCount}</span>}
          </div>

          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="h-7 gap-1 px-2 text-[11px] text-tone-blue rounded-lg hover:bg-tone-blue-soft hover:text-tone-blue"
            >
              {markingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
              Mark all read
            </Button>
          )}
        </div>

        {/* ───────── Body ───────── */}

        <TooltipProvider delayDuration={400}>
          <div className="custom-scrollbar flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-tone-blue-soft">
                  <Loader2 size={17} className="animate-spin text-tone-blue" />
                </div>

                <p className="mt-3 text-xs text-text-muted">Loading notifications...</p>
              </div>
            ) : error ? (
              /* A failed fetch, never an empty inbox — "Try again" only ever
                 appears here. An empty list reads as "You're all caught up"
                 below, with no retry to make the user wonder what broke. */
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-tone-rose-soft text-tone-rose">
                  <AlertTriangle size={17} />
                </div>

                <p className="mt-3 text-xs font-medium text-text-secondary">Couldn't load notifications</p>

                <p className="mt-1 max-w-[240px] text-[11px] leading-4 text-text-muted">{error}</p>

                <Button variant="link" size="sm" onClick={fetchNotifications} className="mt-2 h-auto p-0 text-tone-blue">
                  Try again
                </Button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background-tertiary text-text-muted">
                  <Inbox size={20} />
                </div>

                <p className="mt-4 text-sm font-medium text-text-secondary">You're all caught up</p>

                <p className="mt-1 max-w-[220px] text-[11px] leading-4 text-text-muted">New notifications will appear here when there's something you need to know.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} onOpen={handleOpen} />
                ))}

                {/* The inbox grows with the tenant's work. Without this, page 1
                    was all a user could ever reach — everything older was
                    unreachable from the app. */}
                {page < totalPages && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadPage(page + 1)}
                    disabled={loadingMore}
                    className="mt-1 h-8 w-full gap-1.5 rounded-lg text-[11px] text-tone-blue hover:bg-tone-blue-soft hover:text-tone-blue"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 size={12} className="animate-spin" /> Loading older
                      </>
                    ) : (
                      "Load older notifications"
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
